import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  styled,
  Tabs,
  Tab,
  Fade,
} from "@mui/material";
import useSliderBooks from "../../../hooks/api-request/useSliderBooks";
import HeroSkeleton from "../../../components/skeletons/HeroSkeleton";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const HeroWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRtl",
})(({ theme, isRtl }) => ({
  background: "linear-gradient(135deg, #1E1E1E 0%, #2D2D2D 100%)",
  position: "relative",
  overflow: "hidden",
  height: { xs: "100vh", md: "600px" },
  color: "#fff",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: isRtl ? "auto" : 0,
    left: isRtl ? 0 : "auto",
    width: "40%",
    height: "100%",
    background: "linear-gradient(135deg, #FF4C00 0%, #FF8A00 100%)",
    transform: isRtl 
      ? "skewX(15deg) translateX(-10%)" 
      : "skewX(-15deg) translateX(10%)",
    [theme.breakpoints.down("md")]: {
      width: "120%",
      transform: "skewX(-15deg) translateX(50%)",
      height: "40%",
      right: "auto",
      left: 0,
      top: 0,
      opacity: 0.8,
    },
  },
}));

const ContentWrapper = styled(Container)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: "100%",
  gap: theme.spacing(8),
  padding: theme.spacing(4, 6),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    justifyContent: "center",
    height: "80vh",
    maxHeight: "100vh",
    overflow: "hidden",
  },
}));

const TextContent = styled(Box)(({ theme }) => ({
  flex: "0 0 50%",
  maxWidth: "550px",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    flex: "none",
    textAlign: "center",
    alignItems: "center",
    maxWidth: "100%",
    width: "100%",
    marginTop: 0,
    marginBottom: theme.spacing(1),
    height: "45%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
}));

const ImageContent = styled(Box)(({ theme }) => ({
  flex: "0 0 45%",
  position: "relative",
  zIndex: 2,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "& img": {
    maxWidth: "450px",
    width: "100%",
    height: "auto",
    maxHeight: "500px",
    objectFit: "contain",
    borderRadius: theme.spacing(2),
    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
  },
  [theme.breakpoints.down("md")]: {
    flex: "none",
    width: "100%",
    maxWidth: "260px",
    margin: "0 auto",
    marginTop: theme.spacing(1),
    height: "45%",
    "& img": {
      maxHeight: "100%",
      width: "auto",
      maxWidth: "100%",
      objectFit: "contain",
    },
  },
}));

const ColoredTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  fontWeight: 700,
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(1),
  },
}));

const ViewOffersButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  padding: theme.spacing(1.5, 4),
  fontSize: "1.1rem",
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: "50px",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(255, 76, 0, 0.3)",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "280px",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    fontSize: "1rem",
    padding: theme.spacing(1.2, 3),
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginTop: theme.spacing(4),
  borderTop: `1px solid ${theme.palette.grey[800]}`,
  width: "100%",
  "& .MuiTab-root": {
    color: "#fff",
    opacity: 0.7,
    textTransform: "none",
    fontSize: { xs: "0.875rem", md: "1rem" },
    padding: theme.spacing(2),
    minWidth: "auto",
    "&.Mui-selected": {
      color: "#fff",
      opacity: 1,
    },
  },
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.primary.main,
    height: 3,
  },
  [theme.breakpoints.down("md")]: {
    marginTop: theme.spacing(2),
    "& .MuiTab-root": {
      minHeight: 48,
    },
    "& .MuiTabs-indicator": {
      height: 3,
      display: "block !important",
      bottom: 0,
    },
  },
}));

const TruncatedDescription = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  opacity: 0.8,
  marginBottom: theme.spacing(4),
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.5,
  minHeight: "auto",
  maxHeight: "4.5em",
  [theme.breakpoints.down("md")]: {
    fontSize: "0.9rem",
    WebkitLineClamp: 2,
    marginBottom: theme.spacing(1),
    maxHeight: "3em",
    width: "100%",
    padding: theme.spacing(0, 1),
    lineHeight: 1.4,
  },
}));

const Hero = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const { data: sliderBooks, isLoading } = useSliderBooks();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';

  const heroContent =
    sliderBooks?.map((book) => ({
      title: book.title,
      subtitle: book.subtitle || "",
      description: book.description,
      image: book.cover_image,
      price: book.price,
      id: book.id,
    })) || [];

  // Auto-slide functionality - only if there's more than one item
  useEffect(() => {
    if (heroContent.length <= 1) return; // Don't set up timer if only one item

    const timer = setInterval(() => {
      setIsChanging(true);
      setTimeout(() => {
        setCurrentTab((prev) => (prev + 1) % heroContent.length);
        setIsChanging(false);
      }, 300);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroContent.length]);

  const handleTabChange = (event, newValue) => {
    setIsChanging(true);
    setTimeout(() => {
      setCurrentTab(newValue);
      setIsChanging(false);
    }, 300);
  };

  // Show loading state or fallback content if needed
  if (isLoading) {
    return <HeroSkeleton />;
  }

  if (heroContent.length === 0) {
    return (
      <HeroWrapper isRtl={isRtl}>
        <ContentWrapper maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "500px",
            }}
          >
            <Typography variant="h5" color="white">
              {t('home.hero.noContent')}
            </Typography>
          </Box>
        </ContentWrapper>
      </HeroWrapper>
    );
  }

  return (
    <HeroWrapper isRtl={isRtl}>
      <ContentWrapper maxWidth="xl">
        <TextContent>
          <Fade in={!isChanging} timeout={500}>
            <Box>
              <ColoredTitle
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" },
                  mb: { xs: 0.5, md: 2 },
                  lineHeight: 1.2,
                }}
              >
                {heroContent[currentTab]?.title}
              </ColoredTitle>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.75rem" },
                  mb: { xs: 0.5, md: 2 },
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                {heroContent[currentTab]?.subtitle}
              </Typography>
              <TruncatedDescription>
                {heroContent[currentTab]?.description}
              </TruncatedDescription>
              <ViewOffersButton
                variant="contained"
                size="large"
                onClick={() =>
                  navigate(`/books/${heroContent[currentTab]?.id}`)
                }
                sx={{
                  borderRadius: "50px",
                  padding: "12px 32px",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "0 4px 12px rgba(255, 76, 0, 0.3)",
                }}
              >
                {t('home.hero.viewDetails')}
              </ViewOffersButton>
            </Box>
          </Fade>

          {/* Only show tabs if there's more than one item */}
          {heroContent.length > 1 && (
            <StyledTabs
              value={currentTab}
              onChange={handleTabChange}
              sx={{
                mt: { xs: 3, md: 6 },
                "& .MuiTab-root": {
                  minWidth: { xs: "100px", md: "120px" },
                  fontSize: { xs: "0.875rem", md: "1rem" },
                },
              }}
            >
              {heroContent.map((content, index) => (
                <Tab
                  key={content.id}
                  label={
                    content.title.length > 20
                      ? `${content.title.substring(0, 20)}...`
                      : content.title
                  }
                />
              ))}
            </StyledTabs>
          )}
        </TextContent>

        <ImageContent>
          <Fade in={!isChanging} timeout={500}>
            <Box
              component="img"
              src={heroContent[currentTab]?.image}
              alt={heroContent[currentTab]?.title}
              sx={{
                transform: {
                  xs: "none",
                  md: "perspective(1000px) rotateY(-12deg)",
                },
                transition: "all 0.4s ease",
                "&:hover": {
                  transform: {
                    xs: "none",
                    md: "perspective(1000px) rotateY(-8deg) translateY(-10px)",
                  },
                },
              }}
            />
          </Fade>
        </ImageContent>
      </ContentWrapper>
    </HeroWrapper>
  );
};

export default Hero;
